/**
 * Allowed Domains entry validation (AB#27244).
 *
 * A malformed entry such as 'my.domain.' used to be savable, and the Cassie Identity
 * Service then rejected the whole CDC request with a 400, so the banner never loaded.
 * The server-side validator may skip invalid entries on newer lines, but this predicate prevents them being saved here in the first place.
 *
 * This predicate mirrors CassieIdentityServiceCore's ValidationService.IsValidHostname,
 * so anything this accepts CIS also accepts. Keep the two in step.
 *
 * Kept as a plain function, separate from the vee-validate registration in index.js,
 * so it can be unit tested without pulling Vue in. See domain.spec.js.
 */

const MAX_LABEL_LENGTH = 63
const LABEL = /^[a-z0-9]+(?:-+[a-z0-9]+)*$/i

/**
 * Is a single Allowed Domains entry well formed?
 *
 * Accepts an optional leading '*.' wildcard, the 'all' and 'localhost' escapes that
 * CIS special-cases, and an optional ':port'. Rejects schemes, paths, empty labels
 * (which is what a trailing dot produces) and labels that start or end with a hyphen.
 *
 * @param {string} value A single entry, not a comma-delimited list.
 * @returns {boolean}
 */
export function isValidDomainEntry (value) {
	if (typeof value !== 'string') return false

	const entry = value.trim()
	if (!entry) return false

	// Strip a leading wildcard only. '*' anywhere else is not a valid hostname character.
	const withoutWildcard = entry.startsWith('*.') ? entry.slice(2) : entry

	// Order matters and mirrors CIS exactly: 'all' is matched against the whole value
	// (so 'all:8080' is rejected), 'localhost' against the value with any port removed
	// (so 'localhost:8080' is accepted).
	if (/^all$/i.test(withoutWildcard)) return true

	// No scheme, no path.
	if (withoutWildcard.includes('://') || withoutWildcard.includes('/') || withoutWildcard.includes('\\')) return false

	const portIndex = withoutWildcard.indexOf(':')
	const host = portIndex === -1 ? withoutWildcard : withoutWildcard.slice(0, portIndex)

	if (/^localhost$/i.test(host)) return true

	// A real domain needs at least one dot; 'localhost' is handled above.
	if (!host.includes('.')) return false

	return host.split('.').every(label =>
		label.length > 0 &&
		label.length <= MAX_LABEL_LENGTH &&
		LABEL.test(label)
	)
}
